import React, { useCallback, useMemo } from "react";

import { WSSelect } from "../WSSelect/WSSelect";

import {
  FilterChangeHandler,
  WSFilterSelect,
  WSFilterSingleValue
} from "./types";

type Props = {
  value?: WSFilterSingleValue;
  onChange: FilterChangeHandler;
} & Omit<WSFilterSelect, "type">;

export const FilterFieldSelect: React.FC<Props> = ({
  name,
  options,
  apiOptions,
  searchable,
  value: uncastedValue,
  onChange
}) => {
  const value = useMemo(() => (uncastedValue ? uncastedValue : null), [
    uncastedValue
  ]);

  // Detect API mode
  const isApiMode = !!apiOptions;

  // Transform options from API or static source
  const selectOptions = useMemo(() => {
    if (isApiMode && apiOptions?.data) {
      return apiOptions.data.map((option) => ({
        label: option.label,
        labels: option.helperText ? [option.helperText] : undefined,
        value: option.name
      }));
    }

    return (options || []).map((option) => ({
      label: option.label,
      labels: option.helperText ? [option.helperText] : undefined,
      value: option.name
    }));
  }, [isApiMode, apiOptions?.data, options]);

  // Pagination handler
  const handleReachMenuEnd = useCallback(() => {
    if (isApiMode && apiOptions?.pagination) {
      if (
        apiOptions.pagination.hasNextPage &&
        !apiOptions.pagination.isFetchingNextPage
      ) {
        apiOptions.pagination.fetchNextPage();
      }
    }
  }, [isApiMode, apiOptions?.pagination]);

  const searchProps = useMemo(() => {
    if (isApiMode) {
      return {
        externalSearchText: apiOptions.search?.searchText,
        onExternalSearchChange: apiOptions.search?.onSearchChange,
        onReachMenuEnd: handleReachMenuEnd,
        isListLoading:
          apiOptions?.pagination.isFetching &&
          !apiOptions?.pagination.isFetchingNextPage
      };
    }

    return {
      internalSearch: searchable
    };
  }, [
    apiOptions?.pagination?.isFetching,
    apiOptions?.pagination?.isFetchingNextPage,
    apiOptions?.search?.onSearchChange,
    apiOptions?.search?.searchText,
    handleReachMenuEnd,
    isApiMode,
    searchable
  ]);

  return (
    <WSSelect
      value={value}
      onChange={(newValue) => {
        onChange(name, newValue || undefined);
      }}
      options={selectOptions}
      // Loading state
      status={isApiMode && apiOptions?.isLoading ? "loading" : undefined}
      // Search props
      {...searchProps}
    />
  );
};
